import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, KeyRound, Loader2, Plus, Trash2, Plug } from 'lucide-react';
import {
  buildCursorMcpConfig,
  createApiKey,
  listApiKeys,
  revokeApiKey,
  type ApiKeyMeta,
} from '@/api/apiKeys';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useAccountRole } from '@/hooks/useAccountRole';
import { SettingsReadOnlyBanner } from '@/components/settings/SettingsReadOnlyBanner';
import { notifications } from '@/utils/notifications';
import { ConfirmModal } from '@/components/ui/ConfirmModal';

export const ApiKeysSettings = ({ embedded = false }: { embedded?: boolean }) => {
  const { t } = useTranslation('settings');
  const { hasFeature, loading: entsLoading } = useEntitlements();
  const { canManageAccount } = useAccountRole();
  const [keys, setKeys] = useState<ApiKeyMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [mcpSnippet, setMcpSnippet] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyMeta | null>(null);
  const [revoking, setRevoking] = useState(false);

  const canRead = hasFeature('publicApiRead');
  const canWriteByPlan = hasFeature('publicApiWrite');
  const canWrite = canWriteByPlan && canManageAccount;
  const readOnlyOrg = canRead && !canManageAccount;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listApiKeys();
      setKeys(rows);
    } catch {
      notifications.error(t('apiKeys.errors.loadFailed'));
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const result = await createApiKey(t('apiKeys.defaultLabel'));
      setNewSecret(result.apiKey);
      setMcpSnippet(buildCursorMcpConfig(result.apiKey));
      notifications.success(t('apiKeys.created'));
      await load();
    } catch {
      notifications.error(t('apiKeys.errors.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await revokeApiKey(revokeTarget.id);
      notifications.success(t('apiKeys.revoked'));
      setRevokeTarget(null);
      await load();
    } catch {
      notifications.error(t('apiKeys.errors.revokeFailed'));
    } finally {
      setRevoking(false);
    }
  };

  const copyText = async (text: string, okMsg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notifications.success(okMsg);
    } catch {
      notifications.error(t('apiKeys.errors.copyFailed'));
    }
  };

  return (
    <div
      id={embedded ? 'api-keys' : undefined}
      className={embedded ? 'space-y-6' : 'mx-auto max-w-3xl space-y-8 px-4 py-8 md:px-8'}
    >
      {!embedded ? (
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-foreground">
            <Plug className="h-7 w-7 text-primary" aria-hidden />
            {t('apiKeys.title')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{t('apiKeys.subtitle')}</p>
        </div>
      ) : (
        <div>
          <h3 className="text-base font-bold text-foreground">{t('apiKeys.title')}</h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t('apiKeys.subtitle')}</p>
        </div>
      )}

      {!entsLoading && !canRead ? (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-foreground">
          {t('apiKeys.upgradeHint')}
        </div>
      ) : null}

      {readOnlyOrg ? <SettingsReadOnlyBanner /> : null}

      <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
        <p>{t('apiKeys.securityNote')}</p>
        <ul className="mt-3 list-disc space-y-1 pl-5">
          <li>{t('apiKeys.securityBullets.read')}</li>
          <li>{t('apiKeys.securityBullets.write')}</li>
          <li>{t('apiKeys.securityBullets.revoke')}</li>
        </ul>
      </div>

      {canWrite ? (
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          disabled={creating || entsLoading || !canRead}
          onClick={() => void handleCreate()}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {t('apiKeys.create')}
        </button>
      </div>
      ) : canRead && !canWriteByPlan ? (
        <p className="text-xs text-muted-foreground">{t('apiKeys.writeRequiresTeam')}</p>
      ) : readOnlyOrg ? (
        <p className="text-xs text-muted-foreground">{t('apiKeys.adminRequired')}</p>
      ) : null}

      {newSecret ? (
        <div className="space-y-4 rounded-2xl border border-primary/40 bg-primary/5 p-5">
          <div className="flex items-start gap-2">
            <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-foreground">{t('apiKeys.secretTitle')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('apiKeys.secretOnce')}</p>
              <code className="mt-3 block break-all rounded-lg bg-muted px-3 py-2 font-mono text-xs">
                {newSecret}
              </code>
              <button
                type="button"
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary"
                onClick={() => void copyText(newSecret, t('apiKeys.copiedKey'))}
              >
                <Copy className="h-3.5 w-3.5" />
                {t('apiKeys.copyKey')}
              </button>
            </div>
          </div>

          {mcpSnippet ? (
            <div>
              <p className="text-sm font-semibold text-foreground">{t('apiKeys.cursorTitle')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('apiKeys.cursorHint')}</p>
              <pre className="mt-3 max-h-64 overflow-auto rounded-lg bg-muted p-3 font-mono text-[11px]">
                {mcpSnippet}
              </pre>
              <button
                type="button"
                className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary"
                onClick={() => void copyText(mcpSnippet, t('apiKeys.copiedMcp'))}
              >
                <Copy className="h-3.5 w-3.5" />
                {t('apiKeys.copyMcp')}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-2xl border border-border bg-card">
        <div className="border-b border-border px-5 py-3">
          <h2 className="text-sm font-bold text-foreground">{t('apiKeys.listTitle')}</h2>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 p-5 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('apiKeys.loading')}
          </div>
        ) : keys.length === 0 ? (
          <p className="p-5 text-sm text-muted-foreground">{t('apiKeys.empty')}</p>
        ) : (
          <ul className="divide-y divide-border">
            {keys.map((k) => (
              <li key={k.id} className="flex items-center justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{k.label || k.previewPrefix}</p>
                  <p className="font-mono text-xs text-muted-foreground">{k.previewPrefix}…</p>
                  {k.lastUsedAt ? (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {t('apiKeys.lastUsed', {
                        date: new Date(k.lastUsedAt).toLocaleString(),
                      })}
                    </p>
                  ) : (
                    <p className="mt-1 text-[11px] text-muted-foreground">{t('apiKeys.neverUsed')}</p>
                  )}
                </div>
                {canWrite ? (
                <button
                  type="button"
                  aria-label={t('apiKeys.revokeAria')}
                  className="rounded-lg p-2 text-destructive hover:bg-destructive/10"
                  onClick={() => setRevokeTarget(k)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmModal
        isOpen={Boolean(revokeTarget)}
        onClose={() => setRevokeTarget(null)}
        title={t('apiKeys.revokeTitle')}
        confirmLabel={t('apiKeys.revokeConfirm')}
        isConfirming={revoking}
        onConfirm={() => void handleRevoke()}
        size="md"
      >
        <p className="text-sm text-muted-foreground">
          {t('apiKeys.revokeBody', { label: revokeTarget?.label ?? revokeTarget?.previewPrefix })}
        </p>
      </ConfirmModal>
    </div>
  );
};
